import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaEdit, FaSave, FaPlay, FaTimes, FaUndo, FaRedo, FaChartBar, FaPlus } from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import FieldSelector from './FieldSelector';
import ReportPreview from './ReportPreview';
import { ReportChart, ChartDisplay } from './charts';
import './css/ReportBuilderPage.css';
import jwt_decode from 'jwt-decode';
import {
  getUserFieldPermissionsByObject,
  filterFieldRowsByFlsRead,
  getFlsReadDeniedLowerSet,
} from '../../utils/permissionCache';

const ReportBuilderPage = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();

  const [reportData, setReportData] = useState(null);
  const [reportTitle, setReportTitle] = useState('New Report');
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [filters, setFilters] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [autoPreview, setAutoPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('outline');
  const [availableFields, setAvailableFields] = useState([]);
  const [availableObjects, setAvailableObjects] = useState([]);
  const [lookupFields, setLookupFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [reportName, setReportName] = useState('');
  const [reportLabel, setReportLabel] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [charts, setCharts] = useState([]);
  const [chartsVersion, setChartsVersion] = useState(0); // Force re-render when charts change
  const [showChartModal, setShowChartModal] = useState(false);
  const [editingChart, setEditingChart] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingChart, setViewingChart] = useState(null);
  const [filterLogic, setFilterLogic] = useState('AND');
  const [customLogic, setCustomLogic] = useState('');
  const [groupingInfo, setGroupingInfo] = useState(null);
  const [summarizedFields, setSummarizedFields] = useState({}); // Track which fields are summarized
  const [summarizedData, setSummarizedData] = useState({}); // Track summarized data for charts
  const [isEditing, setIsEditing] = useState(false);
  const [originalReportData, setOriginalReportData] = useState(null);
  const [savedReportId, setSavedReportId] = useState(null);
  const [addingChart, setAddingChart] = useState(false);

  useEffect(() => {
    if (reportId) {
      loadExistingReport(reportId);
    } else {
      loadReportData();
    }
  }, [reportId]);

  useEffect(() => {
    if (autoPreview && (selectedFields.length > 0 || selectedGroups.length > 0)) {
      generatePreview();
    } else if (!autoPreview && selectedFields.length === 0) {
      // Clear preview data when no fields are selected and auto-preview is off
      setPreviewData([]);
    }
  }, [selectedFields, selectedGroups, autoPreview]);

  const loadAllObjectFields = async (objectNames, reportType) => {
    console.log('Loading all object fields:', objectNames);
    try {
      if (!objectNames || objectNames.length === 0) {
        console.warn('No object names provided');
        return;
      }

      // Use the new multiple objects route to fetch all fields at once
      const objectConfigApi = new DataApi('objectconfig');
      const multipleResponse = await objectConfigApi.post('/fetchconfigs', {
        objectNames: objectNames
      });
      console.log('Multiple response:', multipleResponse);

      if (multipleResponse?.data?.success) {
        const allConfigs = multipleResponse.data.data;

        // Determine primary object name
        const primaryObjectName = reportType.type === 'individual'
          ? reportType.object_name
          : reportType.source_object_name;

        // Flatten all fields from all objects and add object context
        const allFields = [];
        allConfigs.forEach(config => {
          if (config.fields && Array.isArray(config.fields)) {
            config.fields.forEach(field => {
              if (field && field.id) { // Filter out null fields
                allFields.push({
                  ...field,
                  object_name: config.object_name,
                  object_label: config.object_label,
                  field_display_name: `${config.object_label}: ${field.field_label}`,
                  is_primary_object: config.object_name === primaryObjectName
                });
              }
            });
          }
        });

        let filteredFields = allFields;
        try {
          const fls = await getUserFieldPermissionsByObject();
          filteredFields = filterFieldRowsByFlsRead(allFields, fls);
        } catch {
          filteredFields = allFields;
        }
        setAvailableFields(filteredFields);

        // Set available objects with their config IDs
        const objectsWithConfigs = allConfigs.map(config => ({
          object_name: config.object_name,
          object_label: config.object_label,
          object_config_id: config.id,
          is_primary: config.object_name === primaryObjectName
        }));
        setAvailableObjects(objectsWithConfigs);

        console.log('Loaded fields for all objects:', allFields);
        console.log('Loaded objects with configs:', objectsWithConfigs);
        console.log('Number of fields loaded:', allFields.length);
        console.log('Object names from ReportBuilder:', objectNames);
        console.log('Primary object:', primaryObjectName);
      }
    } catch (error) {
      console.error('Error loading all object fields:', error);
    }
  };

  const loadReportData = async () => {
    try {
      setLoading(true);

      // First, try to get the report data from sessionStorage
      const storedReportData = sessionStorage.getItem('selectedReportType');
      console.log('Stored report data:', storedReportData);

      if (storedReportData) {
        const reportData = JSON.parse(storedReportData);
        console.log('Parsed report data:', reportData);

        // Extract report type and object names
        const reportType = reportData.reportType;
        const objectNames = reportData.objectNames || [];

        setReportData(reportType);
        setReportTitle(`New ${reportType.object_label || reportType.source_object_label} Report`);

        console.log('ReportType being set:', reportType);
        console.log('ReportType keys:', Object.keys(reportType));

        // Load fields for all objects using the object names from ReportBuilder
        await loadAllObjectFields(objectNames, reportType);
        setLoading(false);
        return;
      }

      // If no stored report data, load from API and use first one as default
      const reportBuilderApi = new DataApi('reportbuilder');
      const response = await reportBuilderApi.fetchAll('report-types');
      console.log('API response:', response);

      if (response?.data?.success) {
        const reportTypes = response.data.data;
        console.log('Report types from API:', reportTypes);
        if (reportTypes.length > 0) {
          const reportType = reportTypes[0];
          console.log('Using first report type:', reportType);
          setReportData(reportType);
          setReportTitle(`New ${reportType.object_label || reportType.source_object_label} Report`);

          // Create a basic object names list for fallback (just the primary object)
          const primaryObjectName = reportType.type === 'individual'
            ? reportType.object_name
            : reportType.source_object_name;
          const fallbackObjectNames = [primaryObjectName];

          // Load fields for the primary object only
          await loadAllObjectFields(fallbackObjectNames, reportType);
        }
      }
    } catch (error) {
      console.error('Error loading report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExistingReport = async (id) => {
    setLoading(true);
    try {
      const reportBuilderApi = new DataApi('reportbuilder');
      const response = await reportBuilderApi.get(`/${id}`);

      if (response?.data?.success) {
        const report = response.data.data;
        console.log('Loaded existing report:', report);

        // Set basic report information
        setReportTitle(`Edit: ${report.report_label || report.name}`);
        setReportName(report.name);
        setReportLabel(report.report_label || report.name);
        setReportDescription(report.description || '');
        setIsEditing(true);
        setSavedReportId(id);
        setOriginalReportData(report);

        // Set report data structure
        let reportData = {
          id: report.id,
          name: report.name,
          description: report.description,
          object_name: report.object_name,
          report_type: report.report_type,
          is_public: report.is_public,
          type: report.report_type === 'individual' ? 'individual' : 'summary',
          source_object_name: report.object_name,
          source_object_label: report.object_label || report.object_name,
          object_config_id: report.object_config_id,
          // Add audit fields
          createddate: report.createddate,
          lastmodifieddate: report.lastmodifieddate,
          createdby: report.createdby,
          lastmodifiedby: report.lastmodifiedby,
          createdbyid: report.createdbyid,
          lastmodifiedbyid: report.lastmodifiedbyid
        };

        // Check if this is a relationship report and add target object info
        if (report.report_objects && report.report_objects.length > 0) {
          const reportObject = report.report_objects[0];
          reportData = {
            ...reportData,
            type: 'relationship',
            target_object_name: reportObject.target_object_name,
            target_object_label: reportObject.target_object_label || reportObject.target_object_name,
            source_object_label: reportObject.source_object_label || reportData.source_object_label,
            relationship_name: reportObject.relationship_name,
            relationship_label: reportObject.relationship_label,
            relationship_type: reportObject.relationship_type,
            source_field_name: reportObject.source_field_name,
            target_field_name: reportObject.target_field_name
          };
        }
        setReportData(reportData);

        // Set available fields from the API response
        if (report.available_fields && report.available_fields.length > 0) {
          console.log('Using available_fields from API response:', report.available_fields);

          // Flatten the available fields structure for FieldSelector
          const flattenedFields = [];
          report.available_fields.forEach(objConfig => {
            if (objConfig.fields && Array.isArray(objConfig.fields)) {
              objConfig.fields.forEach(field => {
                flattenedFields.push({
                  ...field,
                  object_name: objConfig.object_name,
                  object_label: objConfig.object_label,
                  object_description: objConfig.object_description,
                  field_display_name: `${objConfig.object_label}: ${field.field_label}`,
                  is_primary_object: objConfig.object_name === report.object_name
                });
              });
            }
          });

          console.log('Flattened available fields:', flattenedFields);
          let flattenedFiltered = flattenedFields;
          try {
            const fls = await getUserFieldPermissionsByObject();
            flattenedFiltered = filterFieldRowsByFlsRead(flattenedFields, fls);
          } catch {
            flattenedFiltered = flattenedFields;
          }
          setAvailableFields(flattenedFiltered);

          // Also set available objects for compatibility
          const objects = report.available_fields.map(objConfig => ({
            object_name: objConfig.object_name,
            object_label: objConfig.object_label,
            object_description: objConfig.object_description,
            object_config_id: objConfig.id
          }));
          setAvailableObjects(objects);
        } else {
          console.log('No available_fields in response, using fallback method');
          // Fallback to old method if available_fields is not provided
          const objectNames = report.report_objects?.map(obj => obj.object_name) || [report.object_name];
          await loadAllObjectFields(objectNames, reportData);
        }

        // Set selected fields from report fields
        if (report.fields && report.fields.length > 0) {
          console.log('Raw report fields from API:', report.fields);

          let fields = report.fields.map(field => ({
            id: field.field_config_id,
            field_name: field.field_name,
            field_label: field.field_label,
            data_type: field.data_type,
            object_name: field.object_name,
            is_aggregated: field.is_aggregated,
            aggregate_function: field.aggregate_function,
            is_grouped: field.is_grouped,
            sort_order: field.sort_order
          }));

          try {
            const fls = await getUserFieldPermissionsByObject();
            fields = filterFieldRowsByFlsRead(fields, fls);
          } catch {
            /* keep all */
          }

          console.log('Mapped fields for selectedFields:', fields);

          // Helper function to convert database aggregation values to dropdown values
          const convertAggregationValue = (dbValue) => {
            const mapping = {
              'average': 'avg',
              'sum': 'sum',
              'min': 'min',
              'max': 'max',
              'count': 'count'
            };
            return mapping[dbValue] || dbValue;
          };

          // Set summarized fields first
          const summarized = {};
          fields.forEach(field => {
            console.log('Processing field for summarization:', {
              id: field.id,
              field_name: field.field_name,
              is_aggregated: field.is_aggregated,
              aggregate_function: field.aggregate_function,
              raw_is_aggregated: field.is_aggregated,
              raw_aggregate_function: field.aggregate_function
            });

            // Check if field should be summarized
            if (field.is_aggregated && field.aggregate_function) {
              const convertedValue = convertAggregationValue(field.aggregate_function);
              console.log(`Adding field ${field.field_name} to summarized fields with aggregation: ${field.aggregate_function} -> ${convertedValue}`);
              summarized[field.id] = {
                aggregation: convertedValue
              };
            } else {
              console.log(`Field ${field.field_name} is NOT being added to summarized fields. is_aggregated: ${field.is_aggregated}, aggregate_function: ${field.aggregate_function}`);
            }
          });
          console.log('Final summarized fields object:', summarized);
          setSummarizedFields(summarized);

          // Separate fields into selected fields and groups, and set summaryType for each field
          const selectedFieldsList = fields.filter(field => !field.is_grouped).map(field => ({
            ...field,
            summaryType: field.is_aggregated && field.aggregate_function ? convertAggregationValue(field.aggregate_function) : 'none'
          }));
          const groups = fields.filter(field => field.is_grouped).map(field => ({
            ...field,
            summaryType: field.is_aggregated && field.aggregate_function ? convertAggregationValue(field.aggregate_function) : 'none'
          }));

          console.log('Selected fields (non-grouped) with summaryType:', selectedFieldsList);
          console.log('Grouped fields with summaryType:', groups);

          setSelectedFields(selectedFieldsList);
          setSelectedGroups(groups);
        }

        // Set filters
        if (report.filters && report.filters.length > 0) {
          let filters = report.filters.map(filter => ({
            id: filter.id,
            field_name: filter.field_name,
            field_label: filter.field_label,
            data_type: filter.data_type,
            field_config_id: filter.field_config_id,
            object_name: filter.object_name,
            operator: filter.operator,
            value: filter.value,
            is_editable: filter.is_editable !== undefined ? filter.is_editable : true,
            order_no: filter.order_no
          }));
          try {
            const fls = await getUserFieldPermissionsByObject();
            filters = filters.filter((f) => {
              if (!f.field_name) return true;
              const obj = f.object_name || report.object_name;
              if (!obj) return true;
              const denied = getFlsReadDeniedLowerSet(fls, obj);
              return !denied.has(String(f.field_name).toLowerCase());
            });
          } catch {
            /* keep all */
          }
          setFilters(filters);
        }

        // Set filter logic
        if (report.filter_logic) {
          setFilterLogic(report.filter_logic);
        }

        // Set custom logic
        if (report.custom_logic) {
          setCustomLogic(report.custom_logic);
        }

        // Load saved charts for this report from the charts JSONB field
        loadReportCharts(report);

        // Enable auto-preview for existing reports so data loads immediately
        setAutoPreview(true);
      }
    } catch (error) {
      console.error('Error loading existing report:', error);
      alert('Failed to load report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load saved charts for a report from the charts JSONB field
  const loadReportCharts = (reportData) => {
    try {
      if (reportData && reportData.charts && Array.isArray(reportData.charts)) {
        console.log('Loaded saved charts from report data:', reportData.charts);

        // Ensure charts have all required fields with defaults
        const processedCharts = reportData.charts.map((chart, index) => ({
          ...chart,
          // Ensure each chart has a unique ID
          id: chart.id || `chart_${Date.now()}_${index}`,
          // Ensure required fields have defaults
          showTooltips: chart.showTooltips !== undefined ? chart.showTooltips : true,
          showLabels: chart.showLabels !== undefined ? chart.showLabels : false,
          labelColor: chart.labelColor || '#000000',
          fieldColors: chart.fieldColors || {},
          // Ensure yAxisFields is an array (handle both old and new format)
          yAxisFields: chart.yAxisFields || (chart.yAxisField ? [chart.yAxisField] : []),
          // Ensure xAxisField exists
          xAxisField: chart.xAxisField || '',
          // Ensure axis labels exist
          xAxisLabel: chart.xAxisLabel || '',
          yAxisLabel: chart.yAxisLabel || '',
          // Ensure layout fields exist
          sortOrder: chart.sortOrder || 1,
          columnSpan: chart.columnSpan || 1,
          // Ensure chartType exists
          chartType: chart.chartType || 'bar'
        }));

        console.log('Processed charts with defaults:', processedCharts);
        setCharts(processedCharts);
      } else {
        console.log('No saved charts found in report data');
        setCharts([]);
      }
    } catch (error) {
      console.error('Error loading report charts:', error);
      // Don't show error to user as charts are optional
      setCharts([]);
    }
  };

  const generatePreview = async () => {
    // Only generate preview if we have selected fields
    if (selectedFields.length === 0) {
      setPreviewData([]);
      return;
    }

    // Fetch real data from database instead of using mock data
    await handlePreviewReport();
  };

  const handleSaveReport = () => {
    if (!reportData) {
      alert('No report data available to save');
      return;
    }

    if (selectedFields.length === 0) {
      alert('Please select at least one field to save the report');
      return;
    }

    if (isEditing && originalReportData) {
      // When editing, use existing report values
      setReportName(originalReportData.name || '');
      setReportLabel(originalReportData.report_label || '');
      setReportDescription(originalReportData.description || '');
    } else {
      // When creating new report, use default values
      const defaultLabel = `${reportData.source_object_label || reportData.object_label} Report`;
      const defaultName = generateApiName(defaultLabel);
      const defaultDescription = `Report for ${reportData.source_object_label || reportData.object_label}`;

      setReportName(defaultName);
      setReportLabel(defaultLabel);
      setReportDescription(defaultDescription);
    }

    setShowSaveDialog(true);
  };

  const handleConfirmSave = async () => {
    if (!reportName.trim()) {
      alert('Please enter a report name');
      return;
    }

    if (!reportLabel.trim()) {
      alert('Please enter a report label');
      return;
    }

    if (!isValidApiName(reportName)) {
      alert('Report name must be a valid API name:\n- Start with a letter\n- Only lowercase letters, numbers, and underscores\n- Cannot start or end with underscore\n- Examples: "department_report", "user_analytics"');
      return;
    }

    setSaving(true);
    try {
      // Get the primary object config ID
      const primaryObjectName = reportData.type === 'individual'
        ? reportData.object_name
        : reportData.source_object_name;

      const primaryObject = availableObjects.find(obj => obj.object_name === primaryObjectName);
      const objectConfigId = primaryObject?.object_config_id || null;

      console.log('Primary object name:', primaryObjectName);
      console.log('Primary object found:', primaryObject);
      console.log('Object config ID:', objectConfigId);
      console.log('Full reportData object:', reportData);

      // Prepare report data for saving
      const reportPayload = {
        name: reportName,
        report_label: reportLabel,
        description: reportDescription,
        object_name: primaryObjectName,
        report_type: 'tabular',
        is_public: false,
        object_config_id: objectConfigId,
        filter_logic: filterLogic,
        filter_custom_logic: filterLogic === 'CUSTOM' ? customLogic : null,
        report_objects: [],
        report_fields: [],
        report_filters: []
      };

      // Add report objects - preserve existing ones when editing
      if (reportData.type === 'relationship') {
        const targetObject = availableObjects.find(obj => obj.object_name === reportData.target_object_name);
        const targetObjectConfigId = targetObject?.object_config_id || null;

        console.log('Target object name:', reportData.target_object_name);
        console.log('Target object found:', targetObject);
        console.log('Target object config ID:', targetObjectConfigId);
        console.log('Source field name from reportData:', reportData.source_field_name);
        console.log('Target field name from reportData:', reportData.target_field_name);
        console.log('Available keys in reportData:', Object.keys(reportData));

        // When editing, preserve existing report object IDs if available
        const existingReportObject = isEditing && originalReportData?.report_objects?.[0];

        reportPayload.report_objects = [{
          ...(existingReportObject?.id && { id: existingReportObject.id }), // Preserve existing ID
          object_config_id: targetObjectConfigId,
          source_object_name: reportData.source_object_name,
          target_object_name: reportData.target_object_name,
          relationship_name: reportData.relationship_name,
          relationship_label: reportData.relationship_label,
          relationship_type: reportData.relationship_type,
          source_field_name: reportData.source_field_name || 'id',
          target_field_name: reportData.target_field_name
        }];
      } else if (isEditing && originalReportData?.report_objects) {
        // For individual reports, preserve existing report objects when editing
        reportPayload.report_objects = originalReportData.report_objects.map(obj => ({
          ...obj // Preserve all existing report object data
        }));
      }

      // Add selected fields - preserve existing IDs when editing
      reportPayload.report_fields = selectedFields.map((field, index) => {
        // When editing, try to find existing report field ID
        const existingReportField = isEditing && originalReportData?.fields?.find(
          rf => rf.field_config_id === field.id && rf.object_name === field.object_name
        );

        return {
          ...(existingReportField?.id && { id: existingReportField.id }), // Preserve existing ID
          field_config_id: field.id,
          object_name: field.object_name,
          is_aggregated: !!summarizedFields[field.id],
          aggregate_function: summarizedFields[field.id] ? summarizedFields[field.id].aggregation : null,
          is_grouped: selectedGroups.some(group => group.id === field.id),
          sort_order: index + 1,
          sort_direction: 'ASC'
        };
      });

      // Add group fields - preserve existing IDs when editing
      selectedGroups.forEach((group, index) => {
        if (!reportPayload.report_fields.some(field => field.field_config_id === group.id)) {
          // When editing, try to find existing report field ID for group
          const existingReportField = isEditing && originalReportData?.fields?.find(
            rf => rf.field_config_id === group.id && rf.object_name === group.object_name
          );

          reportPayload.report_fields.push({
            ...(existingReportField?.id && { id: existingReportField.id }), // Preserve existing ID
            field_config_id: group.id,
            object_name: group.object_name,
            is_aggregated: !!summarizedFields[group.id],
            aggregate_function: summarizedFields[group.id] ? summarizedFields[group.id].aggregation : null,
            is_grouped: true,
            sort_order: index + 1,
            sort_direction: 'ASC'
          });
        }
      });

      // Note: Virtual record count fields are not sent to backend
      // The backend automatically calculates record counts when grouping is applied
      // These fields are only used for frontend display and chart generation

      console.log('Saving report with payload:', reportPayload);

      // Add filters (if any) - preserve existing IDs when editing
      if (filters && filters.length > 0) {
        reportPayload.report_filters = filters.map((filter, index) => {
          // When editing, try to find existing report filter ID
          const existingReportFilter = isEditing && originalReportData?.filters?.find(
            rf => rf.field_config_id === filter.field_config_id && rf.operator === filter.operator && rf.value === filter.value
          );

          return {
            ...(existingReportFilter?.id && { id: existingReportFilter.id }), // Preserve existing ID
            field_config_id: filter.field_config_id,
            operator: filter.operator,
            value: filter.value,
            is_editable: filter.is_editable !== undefined ? filter.is_editable : true,
            order_no: index + 1
          };
        });
      }

      // Add charts array to the payload as JSONB field (audit fields are already included in individual charts)
      if (charts && charts.length > 0) {
        reportPayload.charts = charts.map(chart => {
          const baseChartData = {
            id: chart.id,
            title: chart.title,
            chartType: chart.chartType,
            showTooltips: chart.showTooltips,
            showLabels: chart.showLabels,
            labelColor: chart.labelColor,
            legendPosition: chart.legendPosition,
            sortOrder: chart.sortOrder,
            columnSpan: chart.columnSpan,
            // Audit fields are already included from individual chart saves
            createdbyid: chart.createdbyid,
            lastmodifiedbyid: chart.lastmodifiedbyid,
            createddate: chart.createddate,
            lastmodifieddate: chart.lastmodifieddate,
            createdby: chart.createdby,
            lastmodifiedby: chart.lastmodifiedby
          };

          // Add chart-type specific fields
          if (chart.chartType === 'scatter') {
            return {
              ...baseChartData,
              // Scatter chart specific fields
              scatterXAxis: chart.scatterXAxis,
              scatterYAxis: chart.scatterYAxis,
              scatterGroupField: chart.scatterGroupField,
              scatterXAxisLabel: chart.scatterXAxisLabel,
              scatterYAxisLabel: chart.scatterYAxisLabel,
            };
          } else if (chart.chartType === 'donut') {
            return {
              ...baseChartData,
              // Donut chart specific fields
              pieValueField: chart.pieValueField,
              pieSliceField: chart.pieSliceField,
              showValues: chart.showValues,
              showPercentages: chart.showPercentages,
              showTotal: chart.showTotal,
              legendColors: chart.legendColors,
              valueFieldType: chart.valueFieldType,
              // Also include standard fields for compatibility
              xAxisField: chart.xAxisField,
              yAxisFields: chart.yAxisFields,
              xAxisLabel: chart.xAxisLabel,
              yAxisLabel: chart.yAxisLabel,
            };
          } else {
            return {
              ...baseChartData,
              // Regular chart fields (bar, line, pie, etc.)
              xAxisField: chart.xAxisField,
              yAxisFields: chart.yAxisFields,
              xAxisLabel: chart.xAxisLabel,
              yAxisLabel: chart.yAxisLabel,
              fieldColors: chart.fieldColors,
            };
          }
        });
      } else {
        // Ensure charts field is always present (empty array if no charts)
        reportPayload.charts = [];
      }

      console.log('Final report payload:', JSON.stringify(reportPayload, null, 2));

      // Call the API
      const reportBuilderApi = new DataApi('reportbuilder');
      let response;

      if (isEditing && reportId) {
        // Update existing report
        response = await reportBuilderApi.put(`/${reportId}`, reportPayload);
      } else {
        // Create new report
        response = await reportBuilderApi.post('/', reportPayload);
      }

      if (response?.data?.success) {
        const action = isEditing ? 'updated' : 'saved';
        const newReportId = response.data.reportId || reportId;
        alert(`Report ${action} successfully!`);
        console.log(`Report ${action} with ID:`, newReportId);
        setSavedReportId(newReportId);
        setShowSaveDialog(false);

        // Navigate to reports list page after successful save
        navigate('/reports');
      } else {
        throw new Error(response?.data?.message || `Failed to ${isEditing ? 'update' : 'save'} report`);
      }

    } catch (error) {
      console.error('Error saving report:', error);
      const message = error.response?.data?.message || error.message;
      alert('Failed to save report: ' + message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSave = () => {
    setShowSaveDialog(false);
    setReportName('');
    setReportLabel('');
    setReportDescription('');
  };

  // Handle column actions from the header dropdown
  const handleColumnAction = (action, field, value) => {
    console.log('Column action:', action, field, value);

    switch (action) {
      case 'sort':
        // Handle sorting
        console.log(`Sorting by ${field.field_label} ${value}`);
        break;
      case 'group':
        // Handle grouping - add or remove from selectedGroups
        console.log(`Grouping by ${field.field_label} (${value})`);
        if (value === 'rows') {
          // Check if field is already in selectedGroups
          const isAlreadyGrouped = selectedGroups.find(g => g.id === field.id);
          if (isAlreadyGrouped) {
            // Remove from groups
            setSelectedGroups(selectedGroups.filter(g => g.id !== field.id));
            console.log(`Removed ${field.field_label} from groups`);
          } else {
            // Add to groups
            setSelectedGroups([...selectedGroups, { ...field, sortOrder: 'ASC' }]);
            console.log(`Added ${field.field_label} to groups`);
          }
        }
        break;
      case 'summarize':
        // Handle summarization
        console.log(`Summarizing ${field.field_label} with ${value}`);
        setSummarizedFields(prev => ({
          ...prev,
          [field.id]: {
            field: field,
            aggregation: value
          }
        }));
        // Also update the summaryType property on the field in selectedFields
        setSelectedFields(prev => prev.map(f =>
          f.id === field.id ? { ...f, summaryType: value } : f
        ));
        break;
      case 'remove_summarize':
        // Handle removing summarization
        console.log(`Removing summarization from ${field.field_label}`);
        setSummarizedFields(prev => {
          const newFields = { ...prev };
          delete newFields[field.id];
          return newFields;
        });
        // Also update the summaryType property on the field in selectedFields
        setSelectedFields(prev => prev.map(f =>
          f.id === field.id ? { ...f, summaryType: 'none' } : f
        ));
        break;
      case 'move':
        // Handle column movement
        console.log(`Moving ${field.field_label} ${value}`);
        break;
      case 'remove':
        // Handle column removal - remove from both selectedFields and selectedGroups
        console.log(`Removing ${field.field_label}`);
        setSelectedFields(selectedFields.filter(f => f.id !== field.id));
        setSelectedGroups(selectedGroups.filter(g => g.id !== field.id));
        // Also remove from summarized fields if it was summarized
        setSummarizedFields(prev => {
          const newFields = { ...prev };
          delete newFields[field.id];
          return newFields;
        });
        break;
      default:
        console.log('Unknown column action:', action);
    }
  };

  // Generate API-friendly name from label
  const generateApiName = (label) => {
    return label
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/_+/g, '_') // Replace multiple underscores with single
      .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
  };

  // Validate API name format
  const isValidApiName = (name) => {
    const apiNameRegex = /^[a-z][a-z0-9_]*[a-z0-9]$|^[a-z]$/;
    return apiNameRegex.test(name);
  };

  // Handle label change and auto-update name
  const handleLabelChange = (newLabel) => {
    setReportLabel(newLabel);
    // Auto-generate name from label if name hasn't been manually edited
    if (!reportName || reportName === generateApiName(reportLabel)) {
      setReportName(generateApiName(newLabel));
    }
  };

  // Build SQL query for preview with proper table relationships
  const buildPreviewQuery = (objectName, selectedFields, filters = [], limit = 10) => {
    // Get tenant schema from JWT token
    let tenantSchema;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const decoded = jwt_decode(token);
      tenantSchema = decoded.tenantcode;

      if (!tenantSchema) {
        throw new Error('Tenant schema (tenantcode) not found in authentication token');
      }
    } catch (error) {
      console.error('Error getting tenant schema:', error);
      throw new Error(`Failed to get tenant schema: ${error.message}`);
    }

    // Group fields by their object/table
    const fieldsByTable = {};
    selectedFields.forEach(field => {
      const tableName = field.object_name || objectName; // Use field's object_name or fallback to primary object
      if (!fieldsByTable[tableName]) {
        fieldsByTable[tableName] = [];
      }
      fieldsByTable[tableName].push(field);
    });

    console.log('Fields grouped by table:', fieldsByTable);

    // Use relationship information from reportData (passed from ReportBuilder)
    const relationships = {};
    console.log('Full reportData:', reportData);

    if (reportData?.type === 'relationship') {
      // For relationship reports, use the relationship info directly from reportData
      console.log('Report type details:', reportData);
      console.log('source_field_name:', reportData.source_field_name);
      console.log('target_field_name:', reportData.target_field_name);

      if (reportData.target_object_name && reportData.source_field_name && reportData.target_field_name) {
        relationships[reportData.target_object_name] = {
          sourceField: reportData.source_field_name,
          targetField: reportData.target_field_name,
          relationshipType: reportData.relationship_type
        };
      } else {
        // If source_field_name and target_field_name are not available,
        // we need to fetch them from the backend or use defaults
        console.warn('Missing source_field_name or target_field_name in reportData');

        // For now, let's use common defaults based on relationship type
        if (reportData.target_object_name) {
          relationships[reportData.target_object_name] = {
            sourceField: 'id', // Default source field
            targetField: reportData.source_object_name, // Default target field (e.g., 'department')
            relationshipType: reportData.relationship_type
          };
        }
      }
    }

    console.log('Relationship info from reportData:', relationships);

    // Build SELECT clause with table prefixes
    const selectClauses = [];
    Object.entries(fieldsByTable).forEach(([tableName, fields]) => {
      const tableAlias = tableName === objectName ? 'main' : tableName.substring(0, 3); // Use 'main' for primary table, first 3 chars for others
      fields.forEach(field => {
        const fieldName = field.field_name || field.id;
        selectClauses.push(`${tableAlias}."${fieldName}" AS "${tableName}_${fieldName}"`);
      });
    });

    const fieldNames = selectClauses.join(', ');

    // Build FROM clause with JOINs using relationship information
    let fromClause = `${tenantSchema}."${objectName}" AS main`;
    const joins = [];
    console.log('Relationships:', relationships);
    // Add JOINs for related tables using actual relationship data
    Object.keys(fieldsByTable).forEach(tableName => {
      if (tableName !== objectName) {
        const tableAlias = tableName.substring(0, 3);
        const relationship = relationships[tableName];

        console.log(`Building JOIN for table: ${tableName}, relationship:`, relationship);

        if (relationship) {
          // Use actual relationship fields from related_list_config
          const joinCondition = `main."${relationship.sourceField}" = ${tableAlias}."${relationship.targetField}"`;
          console.log(`Using relationship JOIN: ${joinCondition}`);
          joins.push(`LEFT JOIN ${tenantSchema}."${tableName}" AS ${tableAlias} ON ${joinCondition}`);
        } else {
          // Fallback to simple assumption if relationship not found
          const fallbackJoin = `main.id = ${tableAlias}.${objectName}_id`;
          console.log(`Using fallback JOIN: ${fallbackJoin}`);
          joins.push(`LEFT JOIN ${tenantSchema}."${tableName}" AS ${tableAlias} ON ${fallbackJoin}`);
        }
      }
    });

    if (joins.length > 0) {
      fromClause += ' ' + joins.join(' ');
    }

    // Build WHERE clause from filters
    let whereClause = '';
    const queryParams = [];
    let paramIndex = 1;

    if (filters && filters.length > 0) {
      const conditions = [];

      filters.forEach((filter, index) => {
        // Skip filters without field selection
        if (!filter.field_name || !filter.field_config_id) {
          return;
        }

        const fieldName = filter.field_name;
        const filterTableName = filter.object_name || objectName;
        const tableAlias = filterTableName === objectName ? 'main' : filterTableName.substring(0, 3);
        const operator = filter.operator || 'equals';
        const value = filter.value;
        const logic = filter.logic || 'AND';

        let condition = '';
        switch (operator.toLowerCase()) {
          case 'equals':
          case '=':
            condition = `${tableAlias}."${fieldName}" = $${paramIndex}`;
            queryParams.push(value);
            paramIndex++;
            break;
          case 'not_equals':
          case '!=':
            condition = `${tableAlias}."${fieldName}" != $${paramIndex}`;
            queryParams.push(value);
            paramIndex++;
            break;
          case 'contains':
          case 'like':
            condition = `${tableAlias}."${fieldName}" ILIKE $${paramIndex}`;
            queryParams.push(`%${value}%`);
            paramIndex++;
            break;
          case 'starts_with':
            condition = `${tableAlias}."${fieldName}" ILIKE $${paramIndex}`;
            queryParams.push(`${value}%`);
            paramIndex++;
            break;
          case 'ends_with':
            condition = `${tableAlias}."${fieldName}" ILIKE $${paramIndex}`;
            queryParams.push(`%${value}`);
            paramIndex++;
            break;
          case 'greater_than':
          case '>':
            condition = `${tableAlias}."${fieldName}" > $${paramIndex}`;
            queryParams.push(value);
            paramIndex++;
            break;
          case 'less_than':
          case '<':
            condition = `${tableAlias}."${fieldName}" < $${paramIndex}`;
            queryParams.push(value);
            paramIndex++;
            break;
          case 'greater_equal':
          case '>=':
            condition = `${tableAlias}."${fieldName}" >= $${paramIndex}`;
            queryParams.push(value);
            paramIndex++;
            break;
          case 'less_equal':
          case '<=':
            condition = `${tableAlias}."${fieldName}" <= $${paramIndex}`;
            queryParams.push(value);
            paramIndex++;
            break;
          case 'is_null':
            condition = `${tableAlias}."${fieldName}" IS NULL`;
            break;
          case 'is_not_null':
            condition = `${tableAlias}."${fieldName}" IS NOT NULL`;
            break;
          default:
            condition = `${tableAlias}."${fieldName}" = $${paramIndex}`;
            queryParams.push(value);
            paramIndex++;
        }

        // Add logic operator for subsequent filters
        if (index > 0 && condition) {
          conditions.push(logic);
        }

        if (condition) {
          conditions.push(condition);
        }
      });

      if (conditions.length > 0) {
        whereClause = `WHERE ${conditions.join(' ')}`;
      }
    }

    // Build the complete query
    const query = `
      SELECT ${fieldNames}
      FROM ${fromClause}
      ${whereClause}
      ORDER BY main."createddate" DESC
      LIMIT $${paramIndex}
    `;

    queryParams.push(limit);

    console.log('Generated preview query with relationships:', query);
    console.log('Query parameters:', queryParams);

    return { query, params: queryParams };
  };

  const handlePreviewReport = async () => {
    if (!reportData) {
      const error = new Error('No report data available for preview');
      alert(error.message);
      throw error;
    }

    if (selectedFields.length === 0) {
      const error = new Error('Please select at least one field to preview the report');
      alert(error.message);
      throw error;
    }

    setPreviewLoading(true);
    try {
      // Prepare report data in the same format as stored in database
      const reportDataForPreview = await prepareReportDataForPreview();

      console.log('Sending report data for preview:', reportDataForPreview);

      // Use executePreviewQuery with the report data
      const dataApi = new DataApi('object');
      const response = await dataApi.post('/preview/report', {
        reportData: reportDataForPreview,
        limit: 10
      });

      if (response?.data?.success) {
        const rawData = response.data.data;
        const grouping = response.data.grouping;
        console.log('Raw data from API:', rawData);
        console.log('Grouping info from API:', grouping);

        // Data is already limited and formatted by the backend
        setPreviewData(rawData);
        setGroupingInfo(grouping);
        console.log('Preview data set:', rawData);
        console.log('Grouping info set:', grouping);

        // Switch to preview tab
        setActiveTab('preview');

        // Return success to indicate preview completed successfully
        return true;
      } else {
        throw new Error(response?.data?.message || 'Failed to fetch preview data');
      }

    } catch (error) {
      console.error('Error fetching preview data:', error);
      alert('Failed to fetch preview data: ' + error.message);

      // Clear preview data on error
      setPreviewData([]);

      // Re-throw error so handleAddChart knows preview failed
      throw error;
    } finally {
      setPreviewLoading(false);
    }
  };

  const prepareReportDataForPreview = async () => {
    try {
      // Get the primary object config ID
      const primaryObjectName = reportData.type === 'individual'
        ? reportData.object_name
        : reportData.source_object_name;

      const primaryObject = availableObjects.find(obj => obj.object_name === primaryObjectName);
      const objectConfigId = primaryObject?.object_config_id || null;

      // Prepare report data in the same format as database tables
      const reportDataForPreview = {
        report: {
          name: 'Preview Report',
          description: 'Preview report data',
          object_name: primaryObjectName,
          report_type: 'tabular',
          is_public: false,
          object_config_id: objectConfigId,
          filter_logic: filterLogic,
          filter_custom_logic: filterLogic === 'CUSTOM' ? customLogic : null
        },
        reportObjects: [
          {
            object_name: primaryObjectName,
            object_label: primaryObject?.object_label || primaryObjectName,
            is_primary: true
          }
        ],
        reportFields: [],
        reportFilters: []
      };

      // Add report objects for relationship types
      if (reportData.type === 'relationship') {
        const targetObject = availableObjects.find(obj => obj.object_name === reportData.target_object_name);
        const targetObjectConfigId = targetObject?.object_config_id || null;

        reportDataForPreview.reportObjects.push({
          object_name: reportData.target_object_name,
          object_label: targetObject?.object_label || reportData.target_object_name,
          is_primary: false,
          source_object_name: reportData.source_object_name,
          target_object_name: reportData.target_object_name,
          relationship_name: reportData.relationship_name,
          relationship_label: reportData.relationship_label,
          relationship_type: reportData.relationship_type,
          source_field_name: reportData.source_field_name || 'id',
          target_field_name: reportData.target_field_name
        });
      }

      // Add selected fields with field information
      reportDataForPreview.reportFields = selectedFields.map((field, index) => ({
        field_config_id: field.id,
        field_name: field.field_name,
        field_label: field.field_label,
        data_type: field.data_type,
        object_name: field.object_name,
        is_aggregated: !summarizedFields[field.id],
        aggregate_function: summarizedFields[field.id] ? summarizedFields[field.id].aggregation : null,
        is_grouped: selectedGroups.some(group => group.id === field.id),
        sort_order: index + 1,
        sort_direction: 'ASC'
      }));

      // Add group fields
      selectedGroups.forEach((group, index) => {
        if (!reportDataForPreview.reportFields.some(field => field.field_config_id === group.id)) {
          reportDataForPreview.reportFields.push({
            field_config_id: group.id,
            field_name: group.field_name,
            field_label: group.field_label,
            data_type: group.data_type,
            object_name: group.object_name,
            is_aggregated: !!summarizedFields[group.id],
            aggregate_function: summarizedFields[group.id] ? summarizedFields[group.id].aggregation : null,
            is_grouped: true,
            sort_order: index + 1,
            sort_direction: 'ASC'
          });
        }
      });

      // Note: Virtual record count fields are not sent to backend for preview
      // The backend automatically calculates record counts when grouping is applied
      // These fields are only used for frontend display and chart generation

      // Add filters (if any)
      if (filters && filters.length > 0) {
        reportDataForPreview.reportFilters = filters.map((filter, index) => ({
          field_config_id: filter.field_config_id,
          field_name: filter.field_name,
          field_label: filter.field_label,
          data_type: filter.data_type,
          object_name: filter.object_name,
          operator: filter.operator,
          value: filter.value,
          is_editable: filter.is_editable !== undefined ? filter.is_editable : true,
          order_no: index + 1
        }));
      }

      console.log('Prepared report data for preview:', reportDataForPreview);
      return reportDataForPreview;

    } catch (error) {
      console.error('Error preparing report data for preview:', error);
      throw error;
    }
  };

  const saveTemporaryReport = async () => {
    try {
      // Get the primary object config ID
      const primaryObjectName = reportData.type === 'individual'
        ? reportData.object_name
        : reportData.source_object_name;

      const primaryObject = availableObjects.find(obj => obj.object_name === primaryObjectName);
      const objectConfigId = primaryObject?.object_config_id || null;

      // Prepare report data for temporary saving
      const reportPayload = {
        name: `temp_preview_${Date.now()}`,
        report_label: 'Temporary Preview Report',
        description: 'Temporary report for preview purposes',
        object_name: primaryObjectName,
        report_type: 'tabular',
        is_public: false,
        object_config_id: objectConfigId,
        filter_logic: filterLogic,
        filter_custom_logic: filterLogic === 'CUSTOM' ? customLogic : null,
        report_objects: [],
        report_fields: [],
        report_filters: []
      };

      // Add report objects for relationship types
      if (reportData.type === 'relationship') {
        const targetObject = availableObjects.find(obj => obj.object_name === reportData.target_object_name);
        const targetObjectConfigId = targetObject?.object_config_id || null;

        reportPayload.report_objects = [{
          object_config_id: targetObjectConfigId,
          source_object_name: reportData.source_object_name,
          target_object_name: reportData.target_object_name,
          relationship_name: reportData.relationship_name,
          relationship_label: reportData.relationship_label,
          relationship_type: reportData.relationship_type,
          source_field_name: reportData.source_field_name || 'id',
          target_field_name: reportData.target_field_name
        }];
      }

      // Add selected fields
      reportPayload.report_fields = selectedFields.map((field, index) => ({
        field_config_id: field.id,
        object_name: field.object_name,
        is_aggregated: !!summarizedFields[field.id],
        aggregate_function: summarizedFields[field.id] ? summarizedFields[field.id].aggregation : null,
        is_grouped: selectedGroups.some(group => group.id === field.id),
        sort_order: index + 1,
        sort_direction: 'ASC'
      }));

      // Add group fields
      selectedGroups.forEach((group, index) => {
        if (!reportPayload.report_fields.some(field => field.field_config_id === group.id)) {
          reportPayload.report_fields.push({
            field_config_id: group.id,
            object_name: group.object_name,
            is_aggregated: !!summarizedFields[group.id],
            aggregate_function: summarizedFields[group.id] ? summarizedFields[group.id].aggregation : null,
            is_grouped: true,
            sort_order: index + 1,
            sort_direction: 'ASC'
          });
        }
      });

      // Add filters (if any)
      if (filters && filters.length > 0) {
        reportPayload.report_filters = filters.map((filter, index) => ({
          field_config_id: filter.field_config_id,
          operator: filter.operator,
          value: filter.value,
          is_editable: filter.is_editable !== undefined ? filter.is_editable : true,
          order_no: index + 1
        }));
      }

      console.log('Saving temporary report for preview:', reportPayload);

      // Save the temporary report
      const dataApi = new DataApi('reportbuilder');
      const response = await dataApi.post('/createreport', reportPayload);

      if (response?.data?.success) {
        return response.data.reportId;
      } else {
        throw new Error(response?.data?.message || 'Failed to save temporary report');
      }

    } catch (error) {
      console.error('Error saving temporary report:', error);
      throw error;
    }
  };

  const handleRunReport = () => {
    if (savedReportId) {
      navigate(`/viewreport/${savedReportId}?source=reportbuilder`);
    } else {
      alert('Please save the report first before running it.');
    }
  };

  const handleClose = () => {
    // Clear session storage for report builder
    sessionStorage.removeItem('selectedReportType');
    // Navigate back to reports list page
    navigate('/reports');
  };

  // Chart-related functions
  const handleAddChart = async () => {
    setEditingChart(null);

    // Check if we have preview data and summarized data
    const hasPreviewData = previewData && previewData.length > 0;
    const hasSummarizedData = summarizedData && Object.keys(summarizedData).length > 0;

    // If no preview data or summarized data exists, trigger preview first
    if (!hasPreviewData || !hasSummarizedData) {
      console.log('No preview data available. Auto-triggering preview before opening chart modal...');

      // Check if we have selected fields
      if (selectedFields.length === 0) {
        alert('Please select at least one field before adding a chart');
        return;
      }

      // Set loading state
      setAddingChart(true);

      // Trigger preview and wait for it to complete
      try {
        await handlePreviewReport();
        // After preview completes, open the chart modal
        setShowChartModal(true);
      } catch (error) {
        console.error('Error generating preview for chart:', error);
        alert('Failed to generate preview data. Please try clicking Preview button first.');
      } finally {
        setAddingChart(false);
      }
    } else {
      // Preview data already exists, open modal directly
      console.log('Preview data already available. Opening chart modal...');
      setShowChartModal(true);
    }
  };

  const handleEditChart = (chart) => {
    console.log('handleEditChart called with:', chart);
    console.log('Current charts array:', charts);
    setEditingChart(chart);
    setShowChartModal(true);
  };

  const handleSaveChart = (chartData) => {
    console.log('handleSaveChart called with:', { chartData, editingChart });

    if (editingChart) {
      // Update existing chart - ensure we have the editing chart ID
      const editingChartId = editingChart.id;
      console.log('Updating chart with ID:', editingChartId);

      setCharts(prevCharts => {
        const updatedCharts = prevCharts.map(chart => {
          console.log('Checking chart:', { chartId: chart.id, editingChartId, match: chart.id === editingChartId });
          if (chart.id === editingChartId) {
            console.log('Found matching chart, updating:', chart);

            // Chart data already includes proper audit fields from ReportChart component
            return { ...chart, ...chartData };
          }
          return chart;
        });

        console.log('Updated charts:', updatedCharts);
        return updatedCharts;
      });
    } else {
      // Add new chart
      console.log('Adding new chart:', chartData);

      // Chart data already includes proper audit fields from ReportChart component
      setCharts(prevCharts => {
        const newCharts = [...prevCharts, chartData];
        console.log('New charts array:', newCharts);
        return newCharts;
      });
    }

    // Increment charts version to force re-render
    setChartsVersion(prev => prev + 1);

    // Close modal after state update
    setShowChartModal(false);
    setEditingChart(null);

    // Force a small delay to ensure state update is processed and component re-renders
    setTimeout(() => {
      console.log('Chart state update completed, charts count:', charts.length + (editingChart ? 0 : 1));
      console.log('Updated charts array:', charts);
    }, 50);
  };

  const handleDeleteChart = (chartToDelete) => {
    if (window.confirm('Are you sure you want to delete this chart?')) {
      setCharts(prevCharts => prevCharts.filter(chart => chart.id !== chartToDelete.id));
      // Increment charts version to force re-render
      setChartsVersion(prev => prev + 1);
    }
  };

  const handleViewChart = (chart) => {
    setViewingChart(chart);
    setShowViewModal(true);
  };

  const handleCloseChartModal = () => {
    setShowChartModal(false);
    setEditingChart(null);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewingChart(null);
  };

  // Handle summarized data from ReportPreview
  const handleSummarizedDataChange = (data) => {
    console.log('Received summarized data from ReportPreview:', data);
    setSummarizedData(data);
  };

  if (loading) {
    return (
      <div className="report-builder-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading report builder...</p>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="report-builder-page">
        <div className="error-container">
          <h2>No Report Data</h2>
          <p>No report data available.</p>
          <button onClick={() => navigate('/report-builder')} className="btn btn-primary">
            Back to Report Builder
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="report-builder-page">
      {/* Top Header */}
      <div className="report-header">
        <div className="report-title-section">
          <h2 className="report-title">

            {reportTitle}

          </h2>
          {reportData && (
            <h4 className='report-objects' >
              {reportData.type === 'individual'
                ? (reportData.object_label ? `  ${reportData.object_label}` : '')
                : (reportData.source_object_label && reportData.target_object_label
                  ? `  ${reportData.source_object_label} & ${reportData.target_object_label}`
                  : reportData.source_object_label ? ` - ${reportData.source_object_label}` : '')}
            </h4>
          )}
        </div>

        <div className="report-actions">
          <div className="action-buttons">
            <button className="action-btn disabled" disabled>
              <FaUndo />
            </button>
            <button className="action-btn disabled" disabled>
              <FaRedo />
            </button>
            <button
              className="action-btn"
              onClick={handleAddChart}
              disabled={addingChart}
              title={addingChart ? "Loading preview data..." : "Add Chart to Report"}
            >
              <FaChartBar /> {addingChart ? 'Loading...' : 'Add Chart'}
            </button>
            <button
              className="action-btn"
              onClick={handlePreviewReport}
              disabled={previewLoading}
            >
              <FaPlay /> {previewLoading ? 'Loading...' : 'Preview'}
            </button>
            <button
              className="action-btn primary"
              onClick={handleSaveReport}
              disabled={saving}
            >
              <FaSave /> {saving ? 'Saving...' : 'Save & Run'}
            </button>
            <button
              className="action-btn"
              onClick={handleSaveReport}
              disabled={saving}
            >
              <FaSave /> {saving ? 'Saving...' : 'Save'}
            </button>
            <button className="action-btn" onClick={handleClose}>
              <FaTimes /> Close
            </button>
            <button
              className="action-btn primary"
              onClick={handleRunReport}
              disabled={!savedReportId}
              title={!savedReportId ? 'Please save the report first' : 'Run the report'}
            >
              <FaPlay /> Run
            </button>
          </div>

          <div className="preview-toggle">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={autoPreview}
                onChange={(e) => setAutoPreview(e.target.checked)}
              />
              <span className="toggle-slider"></span>
              Auto-Update Preview
            </label>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="report-builder-content">
        {/* Left Panel - Field Selector */}
        <FieldSelector
          reportData={reportData}
          availableFields={availableFields}
          selectedFields={selectedFields}
          setSelectedFields={setSelectedFields}
          selectedGroups={selectedGroups}
          setSelectedGroups={setSelectedGroups}
          filters={filters}
          setFilters={setFilters}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          filterLogic={filterLogic}
          setFilterLogic={setFilterLogic}
          customLogic={customLogic}
          setCustomLogic={setCustomLogic}
          summarizedFields={summarizedFields}
          setSummarizedFields={setSummarizedFields}
          isEditing={isEditing}
        />

        {/* Right Panel - Preview */}
        <ReportPreview
          reportData={reportData}
          selectedFields={selectedFields}
          selectedGroups={selectedGroups}
          previewData={previewData}
          filters={filters}
          previewLoading={previewLoading}
          grouping={groupingInfo}
          onColumnAction={handleColumnAction}
          summarizedFields={summarizedFields}
          summarizedData={summarizedData}
          onSummarizedDataChange={handleSummarizedDataChange}
          charts={charts}
          chartsVersion={chartsVersion}
          onEditChart={handleEditChart}
          onDeleteChart={handleDeleteChart}
          onViewChart={handleViewChart}
        />
      </div>


      {/* Chart Modal */}
      <ReportChart
        isOpen={showChartModal}
        onClose={handleCloseChartModal}
        reportData={reportData}
        onSaveChart={handleSaveChart}
        chartValues={[35000, 61000, 45000]} // Default sample data
        chartLabels={['Store', 'FrontDesk', 'Production']} // Default sample labels
        xAxisLabel="Department"
        yAxisLabel="Total Salary (SUM)"
        chartTitle="Department Salary Analysis"
        summarizedData={summarizedData} // Pass enhanced summarized data
        summarizedFields={summarizedFields} // Pass field definitions for summarized data
        previewData={previewData} // Pass preview data for record count calculation
        selectedGroups={selectedGroups} // Pass selected groups for record count calculation
        editingChart={editingChart} // Pass the chart being edited
      />

      {/* Save Report Dialog */}
      {showSaveDialog && (
        <div className="save-dialog-overlay">
          <div className="save-dialog">
            <div className="dialog-header">
              <h3>Save Report</h3>
              <button className="close-btn" onClick={handleCancelSave}>
                <FaTimes />
              </button>
            </div>

            <div className="dialog-content">
              <div className="form-group">
                <label htmlFor="reportLabel">Report Label *</label>
                <input
                  type="text"
                  id="reportLabel"
                  value={reportLabel}
                  onChange={(e) => handleLabelChange(e.target.value)}
                  placeholder="Enter report label (e.g., Department Report)"
                  className="form-control"
                />
                <small className="form-text">Display name for the report</small>
              </div>

              <div className="form-group">
                <label htmlFor="reportName">Report Name *</label>
                <input
                  type="text"
                  id="reportName"
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  placeholder="Enter report name (e.g., department_report)"
                  className={`form-control ${reportName && !isValidApiName(reportName) ? 'is-invalid' : ''}`}
                />
                <small className="form-text">Internal name for the report (auto-generated from label, can be edited)</small>
                {reportName && !isValidApiName(reportName) && (
                  <small className="form-text text-danger">
                    Invalid format. Must start with letter, use only lowercase letters, numbers, and underscores.
                  </small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="reportDescription">Description</label>
                <textarea
                  id="reportDescription"
                  value={reportDescription}
                  onChange={(e) => setReportDescription(e.target.value)}
                  placeholder="Enter report description"
                  className="form-control"
                  rows="3"
                />
              </div>
            </div>

            <div className="dialog-actions">
              <button
                className="btn btn-secondary"
                onClick={handleCancelSave}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmSave}
                disabled={saving || !reportName.trim() || !reportLabel.trim() || !isValidApiName(reportName)}
              >
                {saving ? 'Saving...' : 'Save Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chart View Modal */}
      {showViewModal && viewingChart && (
        <div className="chart-modal-overlay" onClick={handleCloseViewModal}>
          <div className="chart-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="chart-modal-header">
              <h2>{viewingChart.title}</h2>
              <button
                className="chart-modal-close"
                onClick={handleCloseViewModal}
                title="Close"
              >
                <FaTimes />
              </button>
            </div>
            <div className="chart-modal-body">
              <ChartDisplay
                chart={viewingChart}
                selectedFields={selectedFields}
                summarizedFields={summarizedFields}
                selectedGroups={selectedGroups}
                summarizedData={summarizedData}
                previewData={previewData}
                isPreview={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportBuilderPage;
